/**
 * Load / toggle the per-app Gemini 3.5 Transcribe try-it flag for a signed-in user.
 * Default is off. Unsigned users always see enabled=false and cannot turn it on.
 * Overlay is opt-in; the shared switch shows the HIPAA-compliance notice.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getAppGemini35Transcribe,
  setAppGemini35Transcribe,
} from 'services/preferences/voicePreferencesService';

/**
 * @param {string|null|undefined} userId
 * @param {string} appId
 * @returns {{
 *   enabled: boolean,
 *   loading: boolean,
 *   saving: boolean,
 *   setEnabled: (next: boolean) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export default function useGemini35TranscribeAppFlag(userId, appId) {
  const [enabled, setEnabledState] = useState(false);
  const [loading, setLoading] = useState(Boolean(userId));
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setEnabledState(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const on = await getAppGemini35Transcribe(userId, appId);
      setEnabledState(on);
    } catch {
      setEnabledState(false);
    } finally {
      setLoading(false);
    }
  }, [userId, appId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setEnabled = useCallback(
    async (next) => {
      if (!userId) return;
      const value = next === true;
      setSaving(true);
      try {
        const ok = await setAppGemini35Transcribe(userId, appId, value);
        if (ok) setEnabledState(value);
      } finally {
        setSaving(false);
      }
    },
    [userId, appId],
  );

  return {
    enabled: Boolean(userId) && enabled,
    loading,
    saving,
    setEnabled,
    refresh,
  };
}
